import { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "styled-components";
import { Avatar, Badge, Box, Button, Flex, ModalContext, Text } from "dash-ui";
import { AccountStatusesEnum, formatByMask } from "dash-common";

import { BrokerFragment } from "gql/generated.graphql";
import { BrokerModals } from "pages/brokers/constants/constants";
import {
  BROKER_LICENSE_STATUSES_STYLES,
  PHONE_NUMBER_STATUSES_STYLES,
  SUBSCRIPTION_STATUSES_STYLES,
} from "./PageHeader.constants";

const PageHeader = ({ broker }: { broker?: BrokerFragment }) => {
  const { t } = useTranslation(["pages", "common"]);
  const theme = useTheme();
  const { setPayload, toggleModal } = useContext(ModalContext);
  const { profile, account, subscription } = useMemo(
    () => ({
      profile: broker?.profile,
      account: broker?.account,
      subscriptionProduct: broker?.accountSubscription?.subscriptionProduct,
      subscription: broker?.accountSubscription,
    }),
    [broker]
  );

  const isArchivedBroker = account?.status === AccountStatusesEnum.ARCHIVED;

  // Block / Unblock
  const onClickHeaderActionButton = () => {
    if (broker) {
      setPayload({ brokerId: broker?._id });
      toggleModal(
        true,
        isArchivedBroker
          ? BrokerModals.UNBLOCK_BROKER_MODAL
          : BrokerModals.BLOCK_BROKER_MODAL
      );
    }
  };

  return (
    <Flex p={6} justifyContent="space-between">
      <Flex>
        <Avatar size={112} mr={4} url={profile?.avatarFile?.publicUrl} />
        <Box>
          <Text xl semibold>
            {`${profile?.firstName} ${profile?.lastName}`}
          </Text>
          <Flex mt={0.5} mb={2} alignItems="center">
            <Text semibold color={theme.colors.n80}>
              {t("forms:labels:email")}:
            </Text>
            <Box mx={1}>
              <Text>{account?.email}</Text>
            </Box>
          </Flex>
          {subscription?.status && (
            <Flex mt={0.5} mb={2} alignItems="center">
              <Text semibold color={theme.colors.n80}>
                {t("pages:brokersPage.subscriptionStatus")}:
              </Text>
              <Badge
                ml={1}
                title={t(`common:subscriptionStatuses.${subscription?.status}`)}
                type={SUBSCRIPTION_STATUSES_STYLES[subscription?.status || ""]}
                square
              />
            </Flex>
          )}
          {account?.phone && (
            <Flex mt={0.5} mb={2} alignItems="center">
              <Text semibold color={theme.colors.n80}>
                {t("common:labels:phone")}:
              </Text>
              <Box mx={1}>
                <Text>
                  {account?.phone?.dialCode}{" "}
                  {formatByMask(
                    account?.phone?.number || "",
                    "(###) ###-#####"
                  )}
                </Text>
              </Box>
              <Badge
                title={t(
                  `common:accountPhoneNumberStatuses.${account?.phone?.status}`
                )}
                type={
                  PHONE_NUMBER_STATUSES_STYLES[account?.phone?.status || ""]
                }
                square
              />
            </Flex>
          )}
          {broker?.license && (
            <Flex mt={0.5} mb={2} alignItems="center">
              <Text semibold color={theme.colors.n80}>
                {t("pages:brokersPage.licenseId")}
              </Text>
              <Box mx={1}>
                <Text>{broker?.license?.licenseNumber}</Text>
              </Box>
              <Badge
                title={
                  t(
                    `common:brokerLicenseStatuses.${broker?.license?.licenseStatus}`
                  ) || ""
                }
                type={
                  BROKER_LICENSE_STATUSES_STYLES[
                    broker?.license?.licenseStatus || ""
                  ]
                }
                square
              />
            </Flex>
          )}
        </Box>
      </Flex>
      <Button
        variation="outline"
        colorVariation="danger"
        onClick={onClickHeaderActionButton}
      >
        {isArchivedBroker
          ? t("pages:brokersPage.unblockBroker")
          : t("pages:brokersPage.blockBroker")}
      </Button>
    </Flex>
  );
};

export default PageHeader;
